'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { Alert, Box, Button, Container, CircularProgress } from '@mui/material'

import { selectAuth } from '@/src/redux/slices/authSlice'
import { useAppSelector } from '@/src/redux/store'
import { HeaderContainer } from '@/src/components/common/header/HeaderRouter'
import { AccountsSidePanelWrapper } from '@/src/components/desktop/accounts/sidePanel'
import { ProfileData } from '@/src/types/profile'
import { useGetAccountProfileQuery } from '@/src/api/rtkQuery/profileApi'
import { transformProfileResponse } from '@/src/utils/profile/transformProfileResponse'
import { buildTravellerDisplay } from '@/src/utils/travellers/buildTravellerDisplay'
import useFormatDate from '@/src/hooks/date/useFormatDate'
import { isCleartrip } from '@/src/utils/generalUtils/isCleartrip'

import { ProfileView } from './profile'
import { EditProfileWrapper } from './editProfile'

const SignInModalWrapper = dynamic(
    () => import(/* webpackChunkName: "signin-modal" */ '../../../common/header/variants/flyin/SignInModal/SignInModalWrapper'),
    { ssr: false }
)

interface DesktopProfilePageWrapperProps {
    domainCountry: string
}

const DesktopProfilePageWrapper: React.FC<DesktopProfilePageWrapperProps> = ({ domainCountry }) => {
    const router = useRouter()
    const locale = useLocale()
    const t = useTranslations('account.profile')
    const authState = useAppSelector(selectAuth)
    const isLoggedIn = authState.signedIn

    const [isMounted, setIsMounted] = useState(false)
    const [isSignInOpen, setIsSignInOpen] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    const { formatDate } = useFormatDate()

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setIsMounted(true)
        }
    }, [])

    useEffect(() => {
        if (isMounted && !isLoggedIn) {
            setIsSignInOpen(true)
        }
    }, [isMounted, isLoggedIn])

    const handleSignInClose = useCallback(() => {
        setIsSignInOpen(false)
        if (!isLoggedIn) {
            router.push(`/${locale}`)
        }
    }, [isLoggedIn, router, locale])
    const {
        data: rawProfile,
        isLoading,
        isError,
        refetch
    } = useGetAccountProfileQuery({ fetchControl: isCleartrip() ? 'MAIN' : 'ALL' })

    const profileForView: ProfileData | null = useMemo(() => {
        if (!rawProfile) return null
        return transformProfileResponse(rawProfile)
    }, [rawProfile])

    const derivedProfileView = useMemo(() => {
        if (!profileForView) return null

        const { displayName: fullName, initials } = buildTravellerDisplay({
            title: profileForView.title,
            firstName: profileForView.firstName,
            lastName: profileForView.lastName
        })
        const phoneLabel = profileForView.phone?.number
            ? `+${profileForView.phone.countryCode} ${profileForView.phone.number}`
            : '--'
        const dateOfBirthValue = profileForView.dateOfBirth ? formatDate(profileForView.dateOfBirth, 'DD MMMM YYYY') : '--'

        return { fullName, initials, phoneLabel, dateOfBirthValue }
    }, [profileForView])

    const handleEdit = useCallback(() => {
        setIsEditing(true)
    }, [])

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.default'
            }}
        >
            <HeaderContainer domainCountry={domainCountry} />

            <Box sx={{ flex: 1, bgcolor: 'grey.50' }}>
                <Container
                    maxWidth="xl"
                    disableGutters
                    sx={{
                        py: { xs: 2, md: 3 },
                        px: { xs: 2, md: 3 }
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            gap: { xs: 2, md: 3 },
                            alignItems: 'flex-start'
                        }}
                    >
                        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                            <AccountsSidePanelWrapper activeIndex={2} />
                        </Box>

                        <Box sx={{ flex: 1 }}>
                            {isLoading && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                    <CircularProgress />
                                </Box>
                            )}

                            {(derivedProfileView === null || isError) && !isLoading && (
                                <Alert
                                    severity="error"
                                    sx={{ borderRadius: 2, border: 1, borderColor: 'divider' }}
                                    action={
                                        <Button
                                            color="inherit"
                                            size="small"
                                            onClick={refetch}
                                            sx={{ textTransform: 'none', fontWeight: 600 }}
                                        >
                                            {t('retry')}
                                        </Button>
                                    }
                                >
                                    {t('failedToLoad')}
                                </Alert>
                            )}

                            {!isLoading && !isError && profileForView && (
                                <>
                                    {isEditing ? (
                                        <EditProfileWrapper
                                            initialProfile={profileForView}
                                            onCancel={() => setIsEditing(false)}
                                            onSave={() => setIsEditing(false)}
                                        />
                                    ) : (
                                        derivedProfileView && (
                                            <ProfileView
                                                profile={profileForView}
                                                onEdit={handleEdit}
                                                t={t}
                                                fullName={derivedProfileView.fullName}
                                                initials={derivedProfileView.initials}
                                                phoneLabel={derivedProfileView.phoneLabel}
                                                dateOfBirthValue={derivedProfileView.dateOfBirthValue}
                                            />
                                        )
                                    )}
                                </>
                            )}
                        </Box>
                    </Box>
                </Container>
            </Box>

            {isSignInOpen && <SignInModalWrapper open={isSignInOpen} onClose={handleSignInClose} />}
        </Box>
    )
}

export { DesktopProfilePageWrapper }
export type { DesktopProfilePageWrapperProps }
export default DesktopProfilePageWrapper

